"use client"

import { BREADCRUMB_CONFIG, BreadcrumbConfig, DEFAULT_BREADCRUMB_CONFIG } from "@/constants/breadcrumb-config"
import { t } from "@/languages/i18n"
import Link from "next/link"
import { usePathname, useSearchParams } from "next/navigation"
import { Suspense, useEffect, useState } from "react"

export interface BreadCrumbItem {
  title?: string
  link: string
}

interface Props {
  items: BreadCrumbItem[]
  isOverflow?: boolean
  isDetail?: boolean
  isNotMargin?: boolean
  locale?: string
}

const BreadCrumbView = ({ items, locale, searchTerm }: Props & { searchTerm: string | null }) => {
  const pathname = usePathname()
  const [firstItem] = items
  const [lang, setLang] = useState<string>("en")

  useEffect(() => {
    setLang(document?.documentElement?.lang || "en")
  }, [])

  // Lấy config cho locale hiện tại
  const config: BreadcrumbConfig = BREADCRUMB_CONFIG[locale || "en"] || DEFAULT_BREADCRUMB_CONFIG

  // Kiểm tra isAbsolute dựa trên config
  const isAbsoluteByPath = config.absolutePaths.includes(pathname)
  const isAbsoluteByTitle = config.absoluteTitleKeywords.some(keyword =>
    firstItem?.title?.toLowerCase().includes(keyword.toLowerCase()),
  )
  const isAbsolute = isAbsoluteByPath || isAbsoluteByTitle

  const newListItems = searchTerm ? [...items, { title: searchTerm, link: "" }] : items

  return (
    items && (
      <div
        className={`container flex items-center text-xs bg-transparent sm:text-base gap-2 lg:gap-2.5 p-4 lg:pl-[7.5rem] lg:py-6 ${
          isAbsolute ? "absolute inset-x-0 top-20 z-10 flex-wrap" : ""
        }`}
        style={{ scrollbarWidth: "none" }}
      >
        <Link href="/" className={`${!isAbsolute ? "text-[#8E8E93]" : "text-[#C7C7CC]"}`}>
          {t("Homepage")}
        </Link>
        <span className={`${!isAbsolute ? "text-[#8E8E93]" : "text-[#C7C7CC]"}`}>/</span>

        {newListItems.length > 0 &&
          newListItems.map((item, i) => (
            <div key={i} className="flex items-center gap-2.5">
              <Link
                href={item.link ? item.link.trim() : "#"}
                className={`leading-5 font-normal max-h-6 capitalize ${
                  i === newListItems.length - 1
                    ? `${isAbsolute ? "text-white" : "text-[#242F3E] line-clamp-1 break-all text-ellipsis"}`
                    : `${!isAbsolute ? "text-[#8E8E93]" : "text-[#C7C7CC]"}`
                }
                }`}
              >
                {item.title}
              </Link>
              {i !== newListItems.length - 1 && (
                <span className={`${!isAbsolute ? "text-[#8E8E93]" : "text-[#C7C7CC]"}`}>/</span>
              )}
            </div>
          ))}
      </div>
    )
  )
}

const BreadCrumbWithParams = (props: Props) => {
  const params = useSearchParams()
  const searchTerm = params.get("search")
  return <BreadCrumbView {...props} searchTerm={searchTerm} />
}

// useSearchParams() needs a Suspense ancestor for static generation. The boundary
// lives inside the component so every render site is covered without page edits;
// the fallback renders the same breadcrumb minus the `?search=` tail (the common
// no-search case), so the swap is invisible.
const BreadCrumb = (props: Props) => (
  <Suspense fallback={<BreadCrumbView {...props} searchTerm={null} />}>
    <BreadCrumbWithParams {...props} />
  </Suspense>
)

export default BreadCrumb
